import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth';
import { Layout, MENU } from './components/Layout';
import { Loading, Pending } from './components/ui';
import { LoginPage, ChangePasswordPage, ResetPasswordPage } from './pages/Auth';

// Cada página vira um pedaço separado do build, carregado na primeira visita (o pacote inicial fica menor).
function page<M extends Record<string, unknown>>(loader: () => Promise<M>, name: keyof M & string) {
  return lazy(async () => ({ default: (await loader())[name] as ComponentType }));
}
const InicioPage = page(() => import('./pages/Inicio'), 'InicioPage');
const ConfiguracoesPage = page(() => import('./pages/Configuracoes'), 'ConfiguracoesPage');
const AuditoriaPage = page(() => import('./pages/Auditoria'), 'AuditoriaPage');
const CompaniesPage = page(() => import('./pages/Empresas'), 'CompaniesPage');
const CompanyDetailPage = page(() => import('./pages/Empresas'), 'CompanyDetailPage');
const CompanyFormPage = page(() => import('./pages/Empresas'), 'CompanyFormPage');
const ContactDetailPage = page(() => import('./pages/Contatos'), 'ContactDetailPage');
const ContactsPage = page(() => import('./pages/Contatos'), 'ContactsPage');
const ImportPage = page(() => import('./pages/Importar'), 'ImportPage');
const AgendaPage = page(() => import('./pages/Agenda'), 'AgendaPage');
const AlertsPage = page(() => import('./pages/Agenda'), 'AlertsPage');
const HealthPage = page(() => import('./pages/Temperatura'), 'HealthPage');
const ProposalDetailPage = page(() => import('./pages/Propostas'), 'ProposalDetailPage');
const ProposalsPage = page(() => import('./pages/Propostas'), 'ProposalsPage');
const ContractDetailPage = page(() => import('./pages/Contratos'), 'ContractDetailPage');
const ContractsPage = page(() => import('./pages/Contratos'), 'ContractsPage');
const TemplatesPage = page(() => import('./pages/Contratos'), 'TemplatesPage');
const CampaignDetailPage = page(() => import('./pages/Campanhas'), 'CampaignDetailPage');
const CampaignsPage = page(() => import('./pages/Campanhas'), 'CampaignsPage');
const UnsubscribePage = page(() => import('./pages/Campanhas'), 'UnsubscribePage');
const SupportPage = page(() => import('./pages/Atendimento'), 'SupportPage');
const TicketDetailPage = page(() => import('./pages/Atendimento'), 'TicketDetailPage');
const PortfolioPage = page(() => import('./pages/Carteira'), 'PortfolioPage');
const BillingDetailPage = page(() => import('./pages/Faturamento'), 'BillingDetailPage');
const BillingQueuePage = page(() => import('./pages/Faturamento'), 'BillingQueuePage');
const ReportsPage = page(() => import('./pages/Relatorios'), 'ReportsPage');
const OpportunitiesPage = page(() => import('./pages/Oportunidades'), 'OpportunitiesPage');
const OpportunityDetailPage = page(() => import('./pages/Oportunidades'), 'OpportunityDetailPage');
const OpportunityFormPage = page(() => import('./pages/Oportunidades'), 'OpportunityFormPage');
const RelationshipPage = page(() => import('./pages/Oportunidades'), 'RelationshipPage');

function Future({ title, stage }: { title: string; stage: string }) {
  return (
    <>
      <div className="page-head">
        <h1>{title}</h1>
      </div>
      <Pending title={title} stage={stage} />
    </>
  );
}

export function App() {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Descadastro é público: abre com ou sem login (EML-008).
  if (location.pathname === '/descadastro')
    return (
      <Suspense fallback={<Loading />}>
        <UnsubscribePage />
      </Suspense>
    );
  if (loading) return <Loading />;

  if (!user) {
    return (
      <Routes>
        <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }
  if (user.mustChangePassword) return <ChangePasswordPage forced />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<InicioPage />} />
        {MENU.filter((m) => m.stage).map((m) => (
          <Route key={m.to} path={m.to.slice(1)} element={<Future title={m.label} stage={m.stage!} />} />
        ))}
        <Route path="empresas" element={<CompaniesPage />} />
        <Route path="empresas/nova" element={<CompanyFormPage />} />
        <Route path="empresas/importar" element={<ImportPage />} />
        <Route path="empresas/:id" element={<CompanyDetailPage />} />
        <Route path="empresas/:id/editar" element={<CompanyFormPage />} />
        <Route path="contatos" element={<ContactsPage />} />
        <Route path="contatos/:id" element={<ContactDetailPage />} />
        <Route path="oportunidades" element={<OpportunitiesPage />} />
        <Route path="oportunidades/nova" element={<OpportunityFormPage />} />
        <Route path="oportunidades/:id" element={<OpportunityDetailPage />} />
        <Route path="oportunidades/:id/editar" element={<OpportunityFormPage />} />
        <Route path="relacionamento" element={<RelationshipPage />} />
        <Route path="agenda" element={<AgendaPage />} />
        <Route path="saude" element={<HealthPage />} />
        <Route path="propostas" element={<ProposalsPage />} />
        <Route path="propostas/:id" element={<ProposalDetailPage />} />
        <Route path="contratos" element={<ContractsPage />} />
        <Route path="contratos/:id" element={<ContractDetailPage />} />
        <Route path="modelos" element={<TemplatesPage />} />
        <Route path="atendimento" element={<SupportPage />} />
        <Route path="atendimento/:id" element={<TicketDetailPage />} />
        <Route path="carteira" element={<PortfolioPage />} />
        <Route path="faturamento" element={<BillingQueuePage />} />
        <Route path="faturamento/:id" element={<BillingDetailPage />} />
        <Route path="relatorios" element={<ReportsPage />} />
        <Route path="campanhas" element={<CampaignsPage />} />
        <Route path="campanhas/:id" element={<CampaignDetailPage />} />
        <Route path="alertas" element={<AlertsPage />} />
        <Route path="auditoria" element={<AuditoriaPage />} />
        <Route path="configuracoes" element={<ConfiguracoesPage />} />
        <Route path="minha-senha" element={<ChangePasswordPage />} />
        <Route path="redefinir-senha" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Future title="Página não encontrada" stage="—" />} />
      </Route>
    </Routes>
  );
}
